/** Root component: picks the phone or desktop shell and the screen for the current route. */

import { useEffect } from 'react';
import { SheetProvider } from '../components/sheets.js';
import { BottomTabBar, Sidebar } from '../components/shell.js';
import { Toasts } from '../components/Toasts.js';
import { Ayarlar } from '../screens/Ayarlar.js';
import { AyarlarDesktop } from '../screens/AyarlarDesktop.js';
import { Baglanti } from '../screens/Baglanti.js';
import { Bugun } from '../screens/Bugun.js';
import { type DevTools, DevToolsContext, Diger, SectionScreen } from '../screens/Diger.js';
import { BugunDesktop, EkipDesktop, IsteklerDesktop, OnaylarDesktop } from '../screens/desktop.js';
import { Ekip } from '../screens/Ekip.js';
import { Istekler } from '../screens/Istekler.js';
import { Kisi } from '../screens/Kisi.js';
import { Leadler } from '../screens/Leadler.js';
import { OnayDetay, Onaylar } from '../screens/Onaylar.js';
import { StoreContext, useStore } from './context.js';
import { useIsDesktop } from './media.js';
import { Link, navigate, type Route, useRoute } from './router.js';
import type { PanelStore } from './store.js';

/** /diger on the desktop: the sidebar already lists every page, so the route opens Bugün. */
function DesktopDigerRedirect() {
  useEffect(() => {
    navigate('/', { replace: true });
  }, []);
  return null;
}

function NotFound() {
  return (
    <main className="phone-main not-found">
      <p className="all-done__title">Bu sayfa yok</p>
      <Link to="/" className="btn btn--primary">
        Bugün'e dön
      </Link>
    </main>
  );
}

function PhoneScreen({ route }: { route: Route }) {
  switch (route.name) {
    case 'bugun':
      return <Bugun />;
    case 'onaylar':
      return <Onaylar />;
    case 'onay':
      return <OnayDetay id={route.id} />;
    case 'istekler':
      return <Istekler focus={route.focus} />;
    case 'ekip':
      return route.run === null && OFIS_LIVE ? <OfisFrame phone /> : <Ekip run={route.run} />;
    case 'kisi':
      return <Kisi id={route.id} />;
    case 'diger':
      return <Diger />;
    case 'section':
      return <SectionScreen section={route.section} />;
    case 'leadler':
      return <Leadler tab={route.tab} batch={route.batch} />;
    case 'ayarlar':
      return <Ayarlar tab={route.tab} />;
    case 'baglanti':
      return <Baglanti id={route.id} />;
    case 'notfound':
      return <NotFound />;
  }
}

/**
 * 29 Oct 2026: /ekip is the 3D team office (ofis.html, its own page). The production build goes there; tests and the
 * static preview keep the panel's Ekip screen (no live data behind them).
 */
const OFIS_LIVE = import.meta.env.PROD && import.meta.env.MODE !== 'preview';

/**
 * /ekip in production (29 Oct 2026, Sercan: "soldaki menüm kalsın"): the 3D team office (ofis.html, its own page and
 * bundle) in a frame inside the panel, so the sidebar on the desktop and the tab bar on the phone stay.
 */
function OfisFrame({ phone }: { phone: boolean }) {
  return (
    <div className={phone ? 'ofis-frame ofis-frame--phone' : 'ofis-frame'}>
      <iframe src="/ofis.html" title="Ekip ofisi" className="ofis-frame__view" />
      {phone ? <BottomTabBar active="ekip" /> : null}
    </div>
  );
}

function DesktopScreen({ route }: { route: Route }) {
  switch (route.name) {
    case 'bugun':
      return <BugunDesktop />;
    case 'onaylar':
      return <OnaylarDesktop id={null} />;
    case 'onay':
      return <OnaylarDesktop id={route.id} />;
    case 'istekler':
      return <IsteklerDesktop focus={route.focus} />;
    case 'ekip':
      return route.run === null && OFIS_LIVE ? <OfisFrame phone={false} /> : <EkipDesktop run={route.run} />;
    case 'kisi':
      return <Kisi id={route.id} />;
    case 'diger':
      // Audit F13: on the desktop the sidebar is the Diğer list; the old page repeated it 1:1.
      return <DesktopDigerRedirect />;
    case 'section':
      return <SectionScreen section={route.section} />;
    case 'leadler':
      return <Leadler tab={route.tab} batch={route.batch} />;
    case 'ayarlar':
      return <AyarlarDesktop tab={route.tab} connectionId={null} />;
    case 'baglanti':
      return <AyarlarDesktop tab="baglantilar" connectionId={route.id} />;
    case 'notfound':
      return <NotFound />;
  }
}

function Shell() {
  const route = useRoute();
  const desktop = useIsDesktop();
  const store = useStore();

  // Coming back to the app after copying a reply to Etsy: ask whether it was sent.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') store.onReturn();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [store]);

  if (desktop) {
    return (
      <div className="desk">
        <Sidebar route={route} />
        <div className="desk__main">
          <DesktopScreen route={route} />
        </div>
        <Toasts placement="desktop" />
      </div>
    );
  }
  return (
    <div
      className={
        route.name === 'onay' || route.name === 'baglanti' || (route.name === 'leadler' && route.batch)
          ? 'phone phone--detail'
          : 'phone'
      }
    >
      <PhoneScreen route={route} />
      <Toasts placement={route.name === 'onay' ? 'phone-decision' : 'phone'} />
    </div>
  );
}

export function App({ store, devTools = null }: { store: PanelStore; devTools?: DevTools | null }) {
  return (
    <StoreContext.Provider value={store}>
      <DevToolsContext.Provider value={devTools}>
        <SheetProvider>
          <Shell />
        </SheetProvider>
      </DevToolsContext.Provider>
    </StoreContext.Provider>
  );
}
